import React from 'react';
import styled from 'styled-components';
import Websockets from '../../services/Websockets';
import Messenger from '../../services/Messenger';
import NativeKeyboard from '../../services/NativeKeyboard';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import Constants from '../../submodules/logictry_config/constants';
import UserCache from '../../services/cache/UserCache';
import LogicBaseCollectionCache from '../../services/cache/LogicBaseCollectionCache';
import LogicBaseConversationCache from '../../services/cache/LogicBaseConversationCache';
import LogicBaseMessageCache from '../../services/cache/LogicBaseMessageCache';
import LogicBaseRelationshipCache from '../../services/cache/LogicBaseRelationshipCache';
import LogicBaseBlockCache from '../../services/cache/LogicBaseBlockCache';
import WindowSize from '../../services/WindowSize';
import UserAccount from '../../services/UserAccount';
import { ArrowUpIcon, ArrowDownIcon, ArrowLeftIcon, ChatUnfilledIcon, CompressIcon, ExpandIcon, CloseIcon, ImagesIcon } from '../../styledhtml/Icon';
import UserAvatar from '../../components/UserAvatar';
import ScrollIt from '../../utils/Scroll';
import timeAgo from '../../utils/timeAgo';
import { getConversations, getMessages, getUserFollowees } from '../../services/Pagination';
import Scrollable from '../../components/Scrollable';
import ConfirmDialog from '../ConfirmDialog';
import IconBadgeDot from '../IconBadgeDot';
import LoadingIndicator from '../LoadingIndicator';
import MediaUploadTile from '../MediaUploadTile';
import uploadClipboardImage, {
  getClipboardImageFiles,
  getMessengerUploadContext,
  resolveMessengerMessageContent,
} from '../../utils/imagePaste';
import { chatSendUserMessage } from '../../utils/contentModerationMessages';
const iconOnSurface = Constants.isDarkMode ? { filter: 'invert(1)' } : undefined;
const incomingBubble = Constants.isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(233, 233, 233, 0.5)';
const incomingMuted = Constants.isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(233,233,233,0.5)';
const incomingMutedSoft = Constants.isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(233,233,233,0.35)';

const MESSAGE_EDIT_DELETE_WINDOW_MS = 5 * 60 * 1000;
const SCROLL_BOTTOM_THRESHOLD = 5;

/** Touches above (composer top − this px) belong to the thread; below uses preventDefault so WebKit does not blur the composer. */
const MESSENGER_COMPOSER_TOUCH_ALLOW_MESSAGES_ABOVE_SLACK_PX = 12;

function startOfDayMs(d) {
  const x = new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
}

/** Label for a day divider (older message’s calendar day). */
function formatDaySeparatorLabel(date) {
  const d = new Date(date);
  const now = new Date();
  const today = startOfDayMs(now);
  const msgDay = startOfDayMs(d);
  const diffDays = Math.round((today - msgDay) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/** Clock-style time on the last bubble of a cluster (with day context if not today). */
function formatClusterTimeLabel(date) {
  const d = new Date(date);
  const now = new Date();
  const today = startOfDayMs(now);
  const msgDay = startOfDayMs(d);
  const diffDays = Math.round((today - msgDay) / (24 * 60 * 60 * 1000));
  const timeStr = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 0) return timeStr;
  if (diffDays === 1) return `Yesterday ${timeStr}`;
  if (diffDays < 7) return `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${timeStr}`;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}${d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : ''} ${timeStr}`;
}

const MESSAGE_BUBBLE_RADIUS = '1.125rem';
/** Small radius where stacked bubbles meet (Instagram-style groups). */
const BUBBLE_JOIN_RADIUS = '0.25rem';

const AVATAR_SLOT_PX = 32;
const COMPOSER_ATTACHMENT_THUMB_PX = 56;
const COMPOSER_ATTACHMENT_BUTTON_PX = 36;
const MESSENGER_MAX_ATTACHMENTS = 10;

function sameCalendarDayMessage(prevMsg, msg) {
  return startOfDayMs(new Date(prevMsg.createdTime)) === startOfDayMs(new Date(msg.createdTime));
}

/**
 * Instagram-style bubble radii (shorthand: top-left, top-right, bottom-right, bottom-left).
 * Received (left): inner/right edge stays large; outer/left edge uses small radius where stacked.
 * Sent (right): inner/left edge stays large; outer/right edge uses small radius where stacked.
 */
function clusterBubbleBorderRadius(isSent, firstInGroup, lastInGroup) {
  const R = MESSAGE_BUBBLE_RADIUS;
  const J = BUBBLE_JOIN_RADIUS;
  if (firstInGroup && lastInGroup) return R;
  if (isSent) {
    if (firstInGroup) return `${R} ${R} ${J} ${R}`;
    if (lastInGroup) return `${R} ${J} ${R} ${R}`;
    return `${R} ${J} ${J} ${R}`;
  }
  if (firstInGroup) return `${R} ${R} ${R} ${J}`;
  if (lastInGroup) return `${J} ${R} ${R} ${R}`;
  return `${J} ${R} ${R} ${J}`;
}

const Wrapper = styled.div`
  background: ${Constants.SurfaceColor};
`;

const ChatWrapper = styled.div`
  position: absolute;
  background: ${Constants.SurfaceColor};
  > div {
    > div {
      overflow: auto;
    }
    > div:first-child {
      background: ${Constants.SurfaceColor};
      border-radius: 0.5rem 0.5rem 0 0;
      flex: 1;
      position: absolute;
      margin: auto;
      display: flex;
      flex-direction: column;
      align-items: center;
      > div {
        width: 100%;
        max-width: 48rem;
        flex-shrink: 0;
      }
    }
    > div:last-child {
      border-radius: 0 0 0.5rem 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      bottom: 0rem;
      width: 100%;
      margin: 0;
      align-items: center;
      > div:first-child {
        display: flex;
        align-items: center;
        width: 48rem;
        background: ${Constants.SurfaceColor};
        box-shadow: ${Constants.isDarkMode
          ? '0 10px 15px -3px rgba(255, 255, 255, 0.04), 0 4px 6px -4px rgba(255, 255, 255, 0.04)'
          : 'rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.1) 0px 4px 6px -4px'};
        border: ${Constants.LightBorder};
        border-radius: 1.5rem;
        > div:last-child {
          margin-left: 0.5rem;
        }
      }
      button {
        background: ${Constants.DarkTextColor};
        color: ${Constants.isDarkMode ? '#000' : '#fff'};
        padding: 0.5rem 1rem;
        border-radius: 0.5rem;
      }
    }
  }
`;

const Dots = styled.div`
  > div {
    width: 6px;
    height: 6px;
    background: #999;
    border-radius: 50%;
    animation: bounce 1.2s infinite;
  }

  > div:nth-child(2) { animation-delay: 0.2s; }
  > div:nth-child(3) { animation-delay: 0.4s; }

  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }
`;

export default class LogicBaseMessenger extends LogictryComponent {
  constructor() {
    super();
    this.conversationCache = getConversations();
    this.followerCache = getUserFollowees(UserAccount.account._id);
  }
  /** True when the user was at the bottom before the latest render (updated on scroll). */
  _pinnedToBottom = true;
  _messagesContentEl = null;
  _messagesResizeObserver = null;
  _lastMessagesContentHeight = 0;
  state = {
    message: '',
    showScrollDownButton: false,
    messagePasteUploading: false,
    pendingAttachments: [],
    moderationHint: '',
    sending: false,
  }
  /** Observe message content height so async image loads re-pin when sticky at bottom. */
  attachMessagesContent = (el) => {
    if (this._messagesContentEl === el) return;
    this.teardownMessagesResizeObserver();
    this._messagesContentEl = el;
    if (!el || typeof ResizeObserver === 'undefined') return;
    this._lastMessagesContentHeight = el.getBoundingClientRect().height;
    this._messagesResizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const next = entry.contentRect.height;
      const prev = this._lastMessagesContentHeight;
      this._lastMessagesContentHeight = next;
      if (next <= prev) return;
      if (!this._pinnedToBottom || this._messageScrollPreserve) return;
      this.scrollBottom(true);
    });
    this._messagesResizeObserver.observe(el);
  }
  teardownMessagesResizeObserver = () => {
    if (this._messagesResizeObserver) {
      this._messagesResizeObserver.disconnect();
      this._messagesResizeObserver = null;
    }
    this._messagesContentEl = null;
    this._lastMessagesContentHeight = 0;
  }
  componentDidMount() {
    WindowSize.onStateUpdate(this);
    Websockets.onStateUpdate(this);
    UserCache.onStateUpdate(this);
    Messenger.onStateUpdate(this);
    NativeKeyboard.onStateUpdate(this);
    LogicBaseCollectionCache.onStateUpdate(this);
    LogicBaseConversationCache.onStateUpdate(this);
    LogicBaseRelationshipCache.onStateUpdate(this);
    LogicBaseBlockCache.onStateUpdate(this);
    this.conversationCache.onStateUpdate(this);
    this.followerCache.onStateUpdate(this);
    this.scrollBottom(true);
    clearInterval(this.refreshInterval);
    this.refreshInterval = setInterval(this.refresh, 5000);
    const { userId, conversationId, collectionId, contentId } = Messenger;
    this._messengerRouteKey = `${userId}|${conversationId}|${collectionId}|${contentId}`;
    // window.addEventListener('pointerdown', this.removeActiveMessage);
  }
  componentDidUpdate(prevProps, prevState) {
    const { userId, conversationId, collectionId, contentId } = Messenger;
    const key = `${userId}|${conversationId}|${collectionId}|${contentId}`;
    if (this._messengerRouteKey !== key) {
      const hadPrevious = this._messengerRouteKey != null;
      this._messengerRouteKey = key;
      if (hadPrevious && (this.state.message || this.state.editingMessage || this.state.deleteConfirmMessage || this.state.pendingAttachments?.length)) {
        this.revokeAttachmentPreviews(this.state.pendingAttachments);
        this.setState({ message: '', editingMessage: null, deleteConfirmMessage: null, pendingAttachments: [] });
      }
    }
    if (this._messageScrollPreserve) {
      const el = document.getElementById('logicbasemessenger__messages');
      if (el) {
        const applyOlderLoadScrollRestore = () => {
          if (!this._messageScrollPreserve) return;
          const { height, top } = this._messageScrollPreserve;
          const delta = el.scrollHeight - height;
          if (delta > 0) {
            el.scrollTop = top + delta;
            this._messageScrollPreserve = null;
          }
        };
        applyOlderLoadScrollRestore();
        if (this._messageScrollPreserve) {
          requestAnimationFrame(applyOlderLoadScrollRestore);
        }
      }
    }

    const { editingMessage } = this.state;
    const prevEdit = prevState?.editingMessage;
    if (
      editingMessage
      && (!prevEdit || String(editingMessage._id) !== String(prevEdit._id))
    ) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.syncComposerTextareaHeight());
      });
    }
  }
  componentWillUnmount() {
    WindowSize.offStateUpdate(this);
    Websockets.offStateUpdate(this);
    UserCache.offStateUpdate(this);
    Messenger.offStateUpdate(this);
    NativeKeyboard.offStateUpdate(this);
    LogicBaseCollectionCache.offStateUpdate(this);
    LogicBaseConversationCache.offStateUpdate(this);
    LogicBaseRelationshipCache.offStateUpdate(this);
    LogicBaseBlockCache.offStateUpdate(this);
    this.conversationCache.offStateUpdate(this);
    this.followerCache.offStateUpdate(this);
    this.messageCache?.offStateUpdate(this);
    clearInterval(this.refreshInterval);
    this.teardownMessagesResizeObserver();
    if (this._composerChromeTouchRetentionCleanup) {
      this._composerChromeTouchRetentionCleanup();
      this._composerChromeTouchRetentionCleanup = null;
    }
    this.composerChromeOuterEl = null;
    this.revokeAttachmentPreviews(this.state.pendingAttachments);
    // window.removeEventListener('pointerdown', this.removeActiveMessage);
  }
  removeActiveMessage = () => {
    this.setState({ activeMessage: null });
  }
  refresh = () => {
    this.forceUpdate();
  }
  syncComposerTextareaHeight = () => {
    const ta = this.textareaRef;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 220)}px`;
  }
  revokeAttachmentPreviews = (attachments) => {
    (attachments || []).forEach(({ previewUrl }) => {
      if (previewUrl?.startsWith('blob:')) {
        try { URL.revokeObjectURL(previewUrl); } catch { /* noop */ }
      }
    });
  }
  removePendingAttachment = (id) => {
    this.setState((prev) => {
      const target = prev.pendingAttachments.find((a) => a.id === id);
      if (target) this.revokeAttachmentPreviews([target]);
      return {
        pendingAttachments: prev.pendingAttachments.filter((a) => a.id !== id),
      };
    });
  }
  handleComposerImagePaste = async (e, { conversationId, collectionId }) => {
    const imageFiles = getClipboardImageFiles(e);
    if (!imageFiles.length) return;
    e.preventDefault();
    e.stopPropagation();
    if (!UserAccount.isLoggedIn) return;
    if (this.state.messagePasteUploading) return;
    if (this.state.pendingAttachments.length >= MESSENGER_MAX_ATTACHMENTS) return;
    const uploadContext = getMessengerUploadContext({ conversationId, collectionId });
    if (!uploadContext) return;

    const file = imageFiles[0];
    const attachmentId = `paste-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const previewUrl = URL.createObjectURL(file);
    this.setState((prev) => ({
      messagePasteUploading: true,
      pendingAttachments: [
        ...prev.pendingAttachments,
        { id: attachmentId, previewUrl, url: null, uploading: true },
      ],
    }));

    try {
      const { url } = await uploadClipboardImage(file, {
        mode: 'messenger',
        context: uploadContext,
      });
      this.setState((prev) => {
        if (!prev.pendingAttachments.some((a) => a.id === attachmentId)) return null;
        return {
          pendingAttachments: prev.pendingAttachments.map((a) => (
            a.id === attachmentId ? { ...a, url, uploading: false } : a
          )),
        };
      });
    } catch {
      this.removePendingAttachment(attachmentId);
    } finally {
      this.setState({ messagePasteUploading: false });
    }
  }
  handleComposerAttachmentUpload = (results) => {
    const url = results?.signedUrl?.split('?')[0];
    if (!url) return;
    const attachmentId = `pick-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    this.setState((prev) => {
      if (prev.pendingAttachments.length >= MESSENGER_MAX_ATTACHMENTS) return null;
      if (prev.pendingAttachments.some((a) => a.url === url)) return null;
      return {
        pendingAttachments: [
          ...prev.pendingAttachments,
          { id: attachmentId, previewUrl: null, url, uploading: false },
        ],
      };
    });
  }
  renderMessengerAttachmentButton = (variant, { messengerUploadContext, canAddComposerAttachment }) => {
    if (!messengerUploadContext) return null;
    const inThumbRow = variant === 'thumb';
    const sizePx = inThumbRow ? COMPOSER_ATTACHMENT_THUMB_PX : COMPOSER_ATTACHMENT_BUTTON_PX;
    const iconSize = inThumbRow ? 22 : 24;
    return (
      <div
        style={{
          flexShrink: 0,
          opacity: canAddComposerAttachment ? 1 : 0.45,
          marginLeft: inThumbRow ? 0 : 2,
          marginRight: inThumbRow ? 0 : 4,
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <MediaUploadTile
          onlyAllowImage
          uploadChoiceMenu
          hideUploadText
          disableUpload={!canAddComposerAttachment}
          s3Path={messengerUploadContext.s3Path}
          signingUrl={messengerUploadContext.signingUrl}
          additionalQueryParams={{
            ...(messengerUploadContext.conversationId ? { conversationId: messengerUploadContext.conversationId } : {}),
            ...(messengerUploadContext.collectionId ? { collectionId: messengerUploadContext.collectionId } : {}),
          }}
          uploadIcon={<ImagesIcon style={{ width: iconSize, height: iconSize, ...iconOnSurface }} />}
          style={{
            width: sizePx,
            height: sizePx,
            borderRadius: inThumbRow ? 10 : 8,
            backgroundSize: 'cover',
            backgroundColor: inThumbRow ? 'rgba(233,233,233,0.55)' : 'transparent',
          }}
          onUploadFinish={this.handleComposerAttachmentUpload}
        />
      </div>
    );
  }
  /**
   * WebKit moves focus to BODY after touchend on non-inputs (relatedTarget null).
   * React touch handlers are passive; use non-passive listeners and pointer capture.
   * Only applies within the composer chrome band; touches above (thread) are untouched.
   */
  composerChromeOuterEl = null;
  _composerChromeTouchRetentionCleanup = null;
  shouldPreventDefaultOnComposerChromePointer(clientY, target) {
    const outer = this.composerChromeOuterEl;
    if (!outer || !target) return false;
    const rect = outer.getBoundingClientRect();
    if (clientY < rect.top - MESSENGER_COMPOSER_TOUCH_ALLOW_MESSAGES_ABOVE_SLACK_PX) return false;
    const ta = this.textareaRef;
    if (ta && (target === ta || ta.contains(target))) return false;
    if (target.closest?.('[role="button"]') && outer.contains(target)) return false;
    return true;
  }
  attachComposerChromeTouchRetention = (el) => {
    if (this._composerChromeTouchRetentionCleanup) {
      this._composerChromeTouchRetentionCleanup();
      this._composerChromeTouchRetentionCleanup = null;
    }
    this.composerChromeOuterEl = el || null;
    if (!el || typeof el.addEventListener !== 'function') return;
    const opts = { capture: true, passive: false };
    const onTouchStealGuard = (e) => {
      const t = e.touches?.[0] ?? e.changedTouches?.[0];
      if (!t) return;
      if (!this.shouldPreventDefaultOnComposerChromePointer(t.clientY, e.target)) return;
      e.preventDefault();
    };
    el.addEventListener('touchstart', onTouchStealGuard, opts);
    el.addEventListener('touchend', onTouchStealGuard, opts);
    el.addEventListener('touchcancel', onTouchStealGuard, opts);
    this._composerChromeTouchRetentionCleanup = () => {
      el.removeEventListener('touchstart', onTouchStealGuard, opts);
      el.removeEventListener('touchend', onTouchStealGuard, opts);
      el.removeEventListener('touchcancel', onTouchStealGuard, opts);
    };
  };
  onComposerChromePointerDownCapture = (e) => {
    if (!this.shouldPreventDefaultOnComposerChromePointer(e.clientY, e.target)) return;
    e.preventDefault();
  };
  sendMessage = async (conversation) => {
    if (this.state.editingMessage) {
      return this.editMessage();
    }
    if (this.state.messagePasteUploading || this.state.sending) return;
    const { account } = UserAccount;
    const { userId, conversationId, collectionId, contentId } = Messenger;
    const { message, pendingAttachments } = this.state;
    if (pendingAttachments.some((a) => a.uploading)) return;
    const attachments = pendingAttachments
      .filter((a) => a.url && !a.uploading)
      .map((a) => ({ url: a.url, type: 'image' }));
    const caption = (message || '').trim();
    if (!caption && !attachments.length) return;
    if ((caption.length + attachments.reduce((n, a) => n + a.url.length, 0)) > 4000) return;

    this.setState({ sending: true });
    let result;
    try {
      if (userId) {
        result = await Websockets.sendMessage(userId, caption, conversation?._id, null, attachments);
      } else if (conversationId) {
        result = await Websockets.sendMessage(
          conversation.participants.find((p) => p !== account._id),
          caption,
          conversationId,
          null,
          attachments,
        );
      } else if (collectionId) {
        result = await Websockets.sendMessage(null, caption, null, collectionId, attachments);
      } else if (contentId) {
        result = await Websockets.sendMessage(null, caption, null, null, attachments, contentId);
      }
    } catch {
      this.setState({ sending: false, moderationHint: 'Could not send. Please try again.' });
      return;
    }

    const moderationHint = chatSendUserMessage(result?.error, result?.retryAfterMs) || '';
    if (!result?.success) {
      this.setState({ sending: false, moderationHint });
      return;
    }

    this.revokeAttachmentPreviews(pendingAttachments);
    this.setState({ message: '', pendingAttachments: [], moderationHint: '', sending: false }, () => {
      if (this.textareaRef) {
        this.textareaRef.style.height = 'auto';
      }
    });
  }
  editMessage = async () => {
    const { editingMessage, message, sending } = this.state;
    if (!editingMessage || sending) return;

    const isWithinWindow = (Date.now() - new Date(editingMessage.createdTime).getTime()) <= MESSAGE_EDIT_DELETE_WINDOW_MS;
    if (!isWithinWindow) return;

    this.setState({ sending: true });
    let result;
    try {
      result = await Websockets.editMessage(editingMessage._id, message || '');
    } catch {
      this.setState({ sending: false, moderationHint: 'Could not send. Please try again.' });
      return;
    }

    const moderationHint = chatSendUserMessage(result?.error, result?.retryAfterMs) || '';
    if (!result?.success) {
      this.setState({ sending: false, moderationHint });
      return;
    }

    this.setState({
      editingMessage: null,
      message: '',
      moderationHint: '',
      sending: false,
    });

    if (this.textareaRef) {
      this.textareaRef.style.height = 'auto';
    }
  }
  requestDeleteMessage = (msg) => {
    const isWithinWindow = (Date.now() - new Date(msg.createdTime).getTime()) <= MESSAGE_EDIT_DELETE_WINDOW_MS;
    if (!isWithinWindow) return;
    this.setState({ deleteConfirmMessage: msg });
  }
  performDeleteMessage = (msg) => {
    if (!msg?._id) return;
    Websockets.deleteMessage(msg._id);
    if (this.state.editingMessage?._id === msg._id) {
      this.setState({ editingMessage: null, message: '' });
    }
  }
  scrollBottom = (instant, scrollHeight) => {
    const chatbotMessages = document.getElementById('logicbasemessenger__messages');
    if (!chatbotMessages) return;
    if (instant) {
      chatbotMessages.scrollTop = scrollHeight || chatbotMessages.scrollHeight;
      this._pinnedToBottom = true;
    } else {
      this.setState({ isScrolling: true });
      ScrollIt(chatbotMessages.scrollHeight, chatbotMessages, 700, 'easeInOutQuad', () => {
        this._pinnedToBottom = true;
        this.setState({ isScrolling: false });
      });
    }
  }
  checkScroll = () => {
    const chatbotMessages = document.getElementById('logicbasemessenger__messages');
    if (!chatbotMessages) return this.setState({ showScrollDownButton: false });
    this._pinnedToBottom = chatbotMessages.scrollHeight - chatbotMessages.clientHeight - chatbotMessages.scrollTop < SCROLL_BOTTOM_THRESHOLD;
    this.setState({ showScrollDownButton: !this._pinnedToBottom });

    // Only load older at the true top; a wide threshold + inertial scroll fired getMore before height grew,
    // then preserve was cleared with delta 0 — next paint jumped to scrollTop 0.
    const atVeryTop = chatbotMessages.scrollTop <= 2;
    if (
      atVeryTop
      && !this.state.isScrolling
      && this.messageCache
      && this.messageCache.initialized
      && this.messageCache.showMore
      && this.messageCache.allFound
      && !this._messageScrollPreserve
    ) {
      this._messageScrollPreserve = {
        height: chatbotMessages.scrollHeight,
        top: chatbotMessages.scrollTop,
      };
      this.messageCache.getMore();
      // Pagination does not emit after getMore(); re-render when the next page is already in cache (sync hit).
      queueMicrotask(() => this.forceUpdate());
    }
  }
  onPagination = () => {
    if (this.conversationCache.showMore && this.conversationCache.allFound && this.conversationCache.initialized) {
      this.conversationCache.getMore();
    } else if (!this.conversationCache.showMore && this.conversationCache.allFound && this.conversationCache.initialized) {
      if (this.followerCache.showMore && this.followerCache.allFound && this.followerCache.initialized) {
        this.followerCache.getMore();
      }
    }
  }
  getUserName = (user) => user.fullname || `@${user.username || `${user._id.slice(0, 8)}...`}`;
  render() {
    const { size } = NativeKeyboard;
    const { account } = UserAccount;
    const { style } = this.props;
    const { mobile, tablet } = WindowSize;
    let { view, userId, conversationId, collectionId, contentId } = Messenger;
    const {
      message,
      showScrollDownButton,
      isScrolling,
      editingMessage,
      deleteConfirmMessage,
      messagePasteUploading,
      pendingAttachments,
      moderationHint,
      sending,
    } = this.state;
    const deleteConfirmDialog = (
      <ConfirmDialog
        open={!!deleteConfirmMessage}
        text="Delete this message?"
        onNo={() => this.setState({ deleteConfirmMessage: null })}
        onYes={() => {
          const msg = deleteConfirmMessage;
          this.setState({ deleteConfirmMessage: null });
          if (msg) this.performDeleteMessage(msg);
        }}
      />
    );

    // Keep track of collectionId / contentId socket rooms
    if (collectionId !== this.lastCollectionId) {
      if (collectionId) Websockets.sendCollectionUpdate(collectionId, true);
      if (this.lastCollectionId) Websockets.sendCollectionUpdate(this.lastCollectionId, false);
    }
    this.lastCollectionId = collectionId;
    if (contentId !== this.lastContentId) {
      if (contentId) Websockets.sendContentUpdate(contentId, true);
      if (this.lastContentId) Websockets.sendContentUpdate(this.lastContentId, false);
    }
    this.lastContentId = contentId;

    // Get current Conversations and current Followers
    let currentConversations = this.conversationCache.currentPage;
    const currentFollowerPage = this.followerCache.currentPage;
    if (!currentConversations) return null;
    if (!currentFollowerPage) return null;

    // Always sort by updatedTime
    currentConversations = [...currentConversations].sort((a,b) => new Date(b.updatedTime) - new Date(a.updatedTime));

    // Get the conversations unread
    let conversationsUnread = {};
    currentConversations.forEach(({ _id, updatedTime, readTime, participants }) => {
      const otherParticipantId = participants.find((pid) => pid !== account._id);
      const myRead = readTime?.[account._id];
      if (!myRead || new Date(updatedTime) > new Date(myRead)) {
        conversationsUnread[_id] = otherParticipantId;
      }
    });

    // Check if at least one is unread and update all displays
    const atleastOneUnread = Object.keys(conversationsUnread).length > 0;
    setTimeout(() => Messenger.updateUnread(atleastOneUnread));

    // Determine if the chat should show or be hidden
    let hideChat = false;

    // Get the conversation if user present
    let conversation;
    if (userId) {
      conversation = currentConversations.find(({ participants }) => participants.includes(userId));
      if (!conversation) {
        const conversationQuery = LogicBaseConversationCache.query({ p: userId });
        if (!conversationQuery) hideChat = true;
        conversation = conversationQuery?.[0];
      }
    } else if (conversationId) {
      conversation = LogicBaseConversationCache.get(conversationId);
      if (conversation?.error) {
        setTimeout(() => Messenger.updateView('button'));
        view = 'button';
      }
    }

    if (conversation?._id && !conversation.error) {
      const canonical = LogicBaseConversationCache.objects[String(conversation._id)];
      if (canonical && !canonical.error) conversation = canonical;
    }

    // Mark the open DM as read. Optimistically updates the local model and
    // PATCHes the conversation; server's beforePatch persists rt.{accountId}.
    if (conversation?._id) {
      const myRead = conversation?.readTime?.[account._id];
      const behind = !myRead || new Date(conversation.updatedTime) > new Date(myRead);
      if (behind) {
        const readSyncKey = `${conversation._id}|${new Date(conversation.updatedTime).toISOString()}`;
        if (this._conversationReadSyncKey !== readSyncKey) {
          this._conversationReadSyncKey = readSyncKey;
          if (!conversation.readTime) conversation.readTime = {};
          conversation.readTime[account._id] = new Date();
          LogicBaseConversationCache.update(conversation);
        }
      } else {
        this._conversationReadSyncKey = null;
      }
    } else {
      this._conversationReadSyncKey = null;
    }

    // Get conversation messages
    let messages;
    if (conversation?._id || collectionId || contentId) {
      const messageThreadKey = `${conversation?._id || ''}|${collectionId || ''}|${contentId || ''}`;
      if (this._messageThreadKey !== messageThreadKey) {
        this._messageThreadKey = messageThreadKey;
        this._lastRawNewestMessageId = null;
        this.lastMessageCount = null;
        this._messageScrollPreserve = null;
        this._pinnedToBottom = true;
      }

      // Get Messages
      this.messageCache?.offStateUpdate(this);
      this.messageCache = getMessages(conversation?._id || contentId || collectionId);
      this.messageCache.onStateUpdate(this);
      messages = this.messageCache.currentPage;
      if (messages?.length > 0) {
        this.lastMessagesToDisplay = messages;
        this.lastConversationToDisplay = conversation?._id || contentId || collectionId;
      } else if (this.lastConversationToDisplay === (conversation?._id || contentId || collectionId)) {
        messages = this.lastMessagesToDisplay || messages;
      } else {
        this.lastConversationToDisplay = null;
      }

      // Pagination appends older messages: length grows but messages[0] (newest) stays the same.
      // New / realtime messages change messages[0]; pin scroll only when at bottom or own send.
      const rawNewestId = messages?.[0]?._id ?? null;
      if (messages?.length > (this.lastMessageCount ?? 0)) {
        const hadTrackedNewest = this._lastRawNewestMessageId != null;
        const newestChanged = !hadTrackedNewest || rawNewestId !== this._lastRawNewestMessageId;
        if (newestChanged) {
          const newestIsOwn = account && String(messages?.[0]?.sender) === String(account._id);
          const shouldPin = newestIsOwn || this._pinnedToBottom;
          if (shouldPin) {
            this.scrollBottom(true, 9999999);
            requestAnimationFrame(() => {
              this.scrollBottom(true, 9999999);
              requestAnimationFrame(() => {
                this.scrollBottom(true);
              });
            });
          }
        }
      }
      this._lastRawNewestMessageId = rawNewestId;
      this.lastMessageCount = messages?.length || 0;
    } else {
      this._messageThreadKey = null;
      this._lastRawNewestMessageId = null;
      this.lastConversationToDisplay = null;
      this.lastMessagesToDisplay = null;
      this.lastMessageCount = null;
      this.messageCache?.offStateUpdate(this);
      this.messageCache = null;
    }
    if (this.messageCache && !this.messageCache.initialized) hideChat = true;

    // Get users for followers and current conversations
    const userIdsToGet = new Set(currentFollowerPage.map(({ followee }) => followee));
    currentConversations?.forEach(({ participants }) => {
      participants.forEach((p) => {
        if (p !== account._id) userIdsToGet.add(p);
      });
    });
    if (userId) userIdsToGet.add(userId);
    conversation?.participants.forEach((p) => {
      if (p !== account._id) userIdsToGet.add(p);
    });

    messages?.forEach(({ sender }) => userIdsToGet.add(sender));
    const users = (UserCache.getUsersByIds(Array.from(userIdsToGet)) || []);
    const foundUser = (userId || conversation?.participants) && users.find(({ _id }) => _id === (userId || conversation.participants.find((p) => p !== account._id)));

    // Check private and following as well as blocked
    const theyBlocked = foundUser?._id && LogicBaseBlockCache.query({ br: foundUser._id, be: UserAccount.account._id });
    const youBlocked = foundUser?._id && LogicBaseBlockCache.query({ be: foundUser._id, br: UserAccount.account._id });
    const disableMessenger = (theyBlocked?.[0] || youBlocked?.[0]);

    // Scroll to Bottom on View Change
    if (view !== this.lastView) {
      this.scrollBottom(true, 9999999);
      requestAnimationFrame(() => {
        this.scrollBottom(true, 9999999);
        requestAnimationFrame(() => {
          this.scrollBottom(true);
        });
      });
    }
    this.lastView = view;

    // Users To Display
    let conversationsToDisplay = [];
    currentConversations?.forEach(({ _id, participants, updatedTime }) => {
      const userId = participants.find((pid) => pid !== account._id);
      const user = users.find(({ _id }) => _id === userId);
      if (user) conversationsToDisplay.push({ user, unread: conversationsUnread[_id], updatedTime });
    });
    currentFollowerPage.forEach(({ followee }) => {
      const user = users.find(({ _id }) => _id === followee)
      if (user && !conversationsToDisplay.find(({ user: { _id } }) => _id === user._id)) conversationsToDisplay.push({ user });
    });
    if (!userId && !conversationId && !collectionId && !contentId) hideChat = true;
    if (conversationsToDisplay.length === 0 && this.lastConversationsToDisplay?.length > 0) conversationsToDisplay = this.lastConversationsToDisplay;
    else this.lastConversationsToDisplay = conversationsToDisplay;
    conversationsToDisplay = conversationsToDisplay.filter(({ user }) => user._id !== account._id);

    const mobileView = (mobile ||  view === 'messenger');

    let clampedY = 0;
    if (size) {
      const chatbotMessages = document.getElementById('logicbasemessenger__messages');
      if (chatbotMessages) {
        const last = chatbotMessages.lastElementChild;
        const height = last.offsetTop + last.offsetHeight;
        const transformY = (height - size) - chatbotMessages.clientHeight;
        clampedY = -Math.max(0, Math.min(-size, transformY));
      }
    }
    const chatStyle = {
      paddingTop: mobileView ? '0.75rem' : '1rem',
      paddingLeft: mobileView ? '1rem' : '2rem',
      paddingRight: mobileView ? '1rem' : '2rem',
      paddingBottom: 0,
      inset: mobileView ? '0 0 4rem' : '0 0 5rem',
      willChange: 'transform',
      transition: 'transform 0.25s ease',
      transform: `translateY(${clampedY}px)`,
      overflowAnchor: 'none',
    };
    const usersLive = Websockets.getUsersLiveCached(conversationsToDisplay?.map(({ user }) => user?._id) || []);

    // Check and update typing (suppress while editing an existing message)
    const isTyping = conversation?._id && Websockets.typing.has(conversation._id);
    Websockets.sendTypingUpdate(foundUser?._id, conversation?._id, !!message && !editingMessage);

    const conversationListPadding = mobileView ? '0.75rem 0.5rem 1rem 1rem' : '1rem 1.5rem 1.25rem 2rem';
    const conversationRowPadding = '0.375rem 0';

    const userConversations = <Scrollable
      vertical
      paginationHeight={200}
      onPagination={this.onPagination}
      style={{ position: 'absolute', inset: 0, padding: conversationListPadding }}
    >
      {conversationsToDisplay.length > 0 && conversationsToDisplay.map(({ user, unread, updatedTime }) => {
        if (!user) return null;
        const usersName = this.getUserName(user);
        const updated = updatedTime && new Date(updatedTime);
        const updatedSubtitle = updated && !Number.isNaN(updated.getTime())
          ? (timeAgo(updated) === 'now' ? 'Just now' : `Updated ${timeAgo(updated)}`)
          : null;
        return (
          <div
            style={{ padding: conversationRowPadding, margin: 0, display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', position: 'relative' }}
            key={user._id}
            onClick={() => {
              this.setState({ showScrollDownButton: false });
              Messenger.updateView(Messenger.view, user._id);
            }}
          >
            <UserAvatar user={user} showLive={usersLive[user._id]} />
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0, lineHeight: 1.15 }}>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500, whiteSpace: 'nowrap', lineHeight: 1.15 }}>
                {usersName}
              </div>
              {updatedSubtitle && (
                <div style={{ fontSize: Constants.VerySmallFontSize, fontWeight: 400, color: Constants.MediumTextGray, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.15 }}>
                  {updatedSubtitle}
                </div>
              )}
            </div>
            {unread && (
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ width: 10, height: 10, background: 'red', borderRadius: '50%' }} />
              </div>
            )}
          </div>
        )
      }) || <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ textAlign: 'center' }}>
          <h2>No Followers Yet</h2>
          <p>Follow users to message them</p>
        </span>
      </div>}
    </Scrollable>;

    const isDm = !!(
      conversation?._id
      && conversation.participants?.length === 2
      && !collectionId
    );

    const messengerUploadContext = !editingMessage && getMessengerUploadContext({
      conversationId: conversation?._id || conversationId || undefined,
      collectionId: collectionId || undefined,
      contentId: contentId || undefined,
    });
    const canAddComposerAttachment = !!(
      messengerUploadContext
      && !disableMessenger
      && pendingAttachments.length < MESSENGER_MAX_ATTACHMENTS
      && !messagePasteUploading
    );

    const orderedMessages = messages && [...messages].reverse().filter((m) => {
      if (!m || m.deletedTime) return false;
      return !LogicBaseMessageCache.objectJson[String(m._id)]?.dt;
    });
    const messagesLoading = !!(
      this.messageCache
      && !this.messageCache.allFound
      && !(orderedMessages?.length > 0)
    );
    const collection = collectionId && LogicBaseCollectionCache.get(collectionId);

    const chat = !hideChat && <div
      style={{
        borderRadius: '0rem',
        position: 'absolute',
        inset: '0rem',
        background: Constants.SurfaceColor
      }}
    >
      <div
        id="logicbasemessenger__messages"
        onScroll={this.checkScroll}
        style={chatStyle}
      >
        {/* Block wrapper: keeps row/day-separator margin collapse (flex would double gaps). Observed for sticky-bottom re-pin. */}
        <div
          ref={this.attachMessagesContent}
          style={{ display: 'block', width: '100%' }}
        >
        {messagesLoading && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute',
              inset: 0,
              margin: 'auto',
            }}
          >
            <LoadingIndicator />
          </div>
        )}
        {!messagesLoading && orderedMessages?.length > 0 && orderedMessages.map((message, i) => {
          const { sender, text, attachments, createdTime, editedTime } = message;
          const account = sender === UserAccount.account._id;
          const messageUser = users?.find(({ _id }) => _id === sender);
          const isWithinEditWindow = account && (Date.now() - new Date(createdTime).getTime()) <= MESSAGE_EDIT_DELETE_WINDOW_MS;
          const editedLabel = editedTime ? (
            <span
              style={{
                fontSize: 11,
                fontWeight: 500,
                fontStyle: 'italic',
                marginLeft: '0.25rem',
                opacity: account ? 0.75 : 0.85,
                color: account ? undefined : Constants.MediumTextGray,
              }}
            >
              (edited)
            </span>
          ) : null;
          const prev = i > 0 ? orderedMessages[i - 1] : null;
          const next = i < orderedMessages.length - 1 ? orderedMessages[i + 1] : null;
          const prevSame = !!(prev && prev.sender === sender && sameCalendarDayMessage(prev, message));
          const nextSame = !!(next && next.sender === sender && sameCalendarDayMessage(next, message));
          const showDaySeparator = i > 0 && !sameCalendarDayMessage(prev, message);
          const showClusterTime = !nextSame;
          const showAvatar = !nextSame;
          const firstInGroup = !prevSame;
          const lastInGroup = !nextSame;
          const bubbleRadius = clusterBubbleBorderRadius(account, firstInGroup, lastInGroup);
          const gapTight = mobileView ? '0.125rem' : '0.1875rem';
          const gapLoose = mobileView ? '0.75rem' : '1rem';
          const msgKey = message._id != null ? String(message._id) : `msg-${i}`;
          const { imageUrls, caption } = resolveMessengerMessageContent({ text, attachments });

          return (
            <React.Fragment key={msgKey}>
              {showDaySeparator && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    margin: `${gapLoose} 0`,
                    padding: '0 0.25rem',
                  }}
                >
                  <div style={{ flex: 1, height: 1, background: Constants.HairlineBorderRgba }} />
                  <span
                    style={{
                      color: Constants.MediumTextGray,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      lineHeight: '14px',
                      flexShrink: 0,
                    }}
                  >
                    {formatDaySeparatorLabel(createdTime)}
                  </span>
                  <div style={{ flex: 1, height: 1, background: Constants.HairlineBorderRgba }} />
                </div>
              )}
              <div
                style={{
                  marginBottom: nextSame ? gapTight : gapLoose,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: account ? 'flex-end' : 'flex-start',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      gap: '0.125rem',
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: !account ? '0.5rem' : 0,
                        justifyContent: account ? 'flex-end' : 'flex-start',
                        width: '100%',
                        minWidth: 0,
                      }}
                    >
                      {!account && (
                        <div style={{ width: AVATAR_SLOT_PX, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                          {showAvatar && messageUser ? (
                            <UserAvatar user={messageUser} showLive={usersLive[messageUser._id]} />
                          ) : null}
                        </div>
                      )}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: account ? 'flex-end' : 'flex-start',
                          gap: caption && imageUrls.length ? 6 : 0,
                          flexShrink: 0,
                          width: 'max-content',
                          maxWidth: '70%',
                        }}
                      >
                        {imageUrls.length > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 4,
                              maxWidth: '100%',
                            }}
                          >
                            {imageUrls.map((url, imgIdx) => (
                              <img
                                key={`${msgKey}-img-${imgIdx}`}
                                src={url}
                                alt=""
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: 240,
                                  borderRadius: 10,
                                  display: 'block',
                                }}
                              />
                            ))}
                          </div>
                        )}
                        {caption && (
                          <div
                            style={{
                              backgroundColor: account ? Constants.PrimaryColor : incomingBubble,
                              color: account ? 'white' : undefined,
                              padding: '0.5rem 1rem',
                              borderRadius: bubbleRadius,
                              maxWidth: '100%',
                              wordBreak: 'break-word',
                            }}
                          >
                            <span style={{ whiteSpace: 'pre-wrap' }}>{caption}{editedLabel}</span>
                          </div>
                        )}
                        {!imageUrls.length && !caption && text && (
                          <div
                            style={{
                              backgroundColor: account ? Constants.PrimaryColor : incomingBubble,
                              color: account ? 'white' : undefined,
                              padding: '0.5rem 1rem',
                              borderRadius: bubbleRadius,
                              maxWidth: '100%',
                              wordBreak: 'break-word',
                            }}
                          >
                            <span style={{ whiteSpace: 'pre-wrap' }}>{text}{editedLabel}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        justifyContent: account ? 'flex-end' : 'flex-start',
                        width: '100%',
                        paddingLeft: account ? 0 : `calc(${AVATAR_SLOT_PX}px + 0.5rem)`,
                        paddingRight: 0,
                        boxSizing: 'border-box',
                      }}
                    >
                  {isWithinEditWindow && (
                    <>
                      <div
                        onClick={() => {
                          this.setState({ editingMessage: message, message: text || '' }, () => {
                            requestAnimationFrame(() => {
                              const ta = this.textareaRef;
                              if (!ta) return;
                              ta.focus();
                              const len = (ta.value || '').length;
                              try {
                                ta.setSelectionRange(len, len);
                              } catch { /* noop */ }
                              this.syncComposerTextareaHeight();
                            });
                          });
                        }}
                        style={{
                          color: Constants.MediumTextGray,
                          fontSize: Constants.VerySmallFontSize,
                          padding: '0.25rem',
                          cursor: 'pointer',
                          lineHeight: '18px',
                        }}
                      >
                        edit
                      </div>
                      <div
                        onClick={() => this.requestDeleteMessage(message)}
                        style={{
                          color: Constants.MediumTextGray,
                          fontSize: Constants.VerySmallFontSize,
                          padding: '0.25rem',
                          cursor: 'pointer',
                          lineHeight: '18px',
                        }}
                      >
                        delete
                      </div>
                    </>
                  )}
                  {showClusterTime && (
                    <div
                      style={{
                        color: Constants.MediumTextGray,
                        fontSize: 11,
                        margin: account ? '0 0.25rem' : '0 0.25rem 0 0',
                        lineHeight: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        fontWeight: 500,
                        opacity: 0.85,
                      }}
                    >
                      {formatClusterTimeLabel(createdTime)}
                    </div>
                  )}
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        }) || !messagesLoading && userId && foundUser && <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              position: 'absolute',
              gap: '1rem',
              inset: 0,
              margin: 'auto'
            }}
          >
            <UserAvatar
              widthAndHeight={120}
              user={foundUser}
              showLive={usersLive[foundUser._id]}
            />
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '1rem', whiteSpace: 'nowrap' }}>
              {this.getUserName(foundUser)}
            </div>
        </div> || !messagesLoading && collectionId && collection && !(orderedMessages?.length > 0) && <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              position: 'absolute',
              gap: '0.5rem',
              inset: 0,
              margin: 'auto',
              textAlign: 'center',
              padding: '0 1rem',
            }}
          >
            <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
              {collection.title}
            </div>
            <div style={{ color: Constants.MediumTextGray, fontSize: Constants.SemiSmallFontSize }}>
              No messages yet.
            </div>
        </div>}
        {isTyping && (
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {foundUser && <UserAvatar user={foundUser} showLive={usersLive[foundUser._id]} />}
            <div
              style={{
                backgroundColor: incomingBubble,
                padding: '0.5rem 1rem',
                borderRadius: '1.5rem',
                fontSize: 14,
                color: Constants.MediumTextGray
              }}
            >
              <Dots style={{ display: 'flex', gap: 2 }}>
                <div />
                <div />
                <div />
              </Dots>
            </div>
          </div>
        )}
        <div style={{ height: '4rem', flexShrink: 0 }}></div>
        </div>
      </div>
      {(showScrollDownButton && !isScrolling) && <div
        onClick={() => this.scrollBottom()}
        style={{
          position: 'absolute',
          bottom: mobileView ? 'calc(4rem + 1.5rem)' : 'calc(5rem + 2.5rem)',
          left: 'calc(50% - 1rem)',
          height: '2rem',
          width: '2rem',
          background: Constants.SurfaceColor,
          border: Constants.LightBorder,
          borderRadius: '50%',
          padding: '0.4rem',
          cursor: 'pointer',
          zIndex: 2,
        }}
      >
        <ArrowDownIcon style={iconOnSurface} />
      </div>}
      <div
        ref={this.attachComposerChromeTouchRetention}
        onPointerDownCapture={this.onComposerChromePointerDownCapture}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: mobileView ? '0 0 1rem' : '0 0 2rem',
          willChange: 'transform',
          transform: `translateY(${size}px)`,
          transition: 'transform 0.25s ease',
          pointerEvents: disableMessenger && 'none' || null,
        }}>
        <div
          style={{
            maxWidth: mobileView ? 'calc(100% - 2rem)' : 'calc(100% - 4rem)',
            padding: mobileView ? '0.5rem 0.75rem 0.5rem 1rem' : '1rem 1rem 1rem 1.5rem',
            background: disableMessenger ? incomingMuted : Constants.SurfaceColor,
            color: disableMessenger ? (Constants.isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)') : 'inherit',
            cursor: disableMessenger ? 'not-allowed' : 'text',
            borderRadius: editingMessage ? '0.75rem' : undefined,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: editingMessage ? 4 : (pendingAttachments.length ? 8 : 0),
            minWidth: 0,
            flexShrink: 0,
            alignSelf: 'center',
          }}
          onKeyDown={(e) => {
            if (WindowSize.mobile) return;
            if (e.metaKey || e.ctrlKey || e.shiftKey) return;
            if (e.key !== 'Enter') return;
            e.stopPropagation();
            e.preventDefault();
            if (disableMessenger) return;
            this.sendMessage(conversation);
          }}
        >
          {editingMessage && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem',
                flexShrink: 0,
                fontSize: 11,
                lineHeight: '14px',
                color: Constants.MediumTextGray,
              }}
            >
              <span style={{ fontWeight: 600 }}>Editing</span>
              <div
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  this.setState({ editingMessage: null, message: '' }, () => {
                    if (this.textareaRef) this.textareaRef.style.height = 'auto';
                  });
                }}
                onClick={() => {
                  this.setState({ editingMessage: null, message: '' }, () => {
                    if (this.textareaRef) this.textareaRef.style.height = 'auto';
                  });
                }}
                style={{ cursor: 'pointer', color: Constants.PrimaryColor, fontWeight: 600 }}
              >
                Cancel
              </div>
            </div>
          )}
          {!editingMessage && pendingAttachments.length > 0 && (
            <div
              style={{
                paddingTop: 2,
                paddingBottom: 2,
                flexShrink: 0,
                overflow: 'visible',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  overflowX: 'auto',
                  paddingTop: 2,
                  paddingBottom: 2,
                }}
              >
              {!editingMessage && this.renderMessengerAttachmentButton('thumb', { messengerUploadContext, canAddComposerAttachment })}
              {pendingAttachments.map((attachment) => {
                const src = attachment.url || attachment.previewUrl;
                return (
                  <div
                    key={attachment.id}
                    style={{
                      position: 'relative',
                      width: COMPOSER_ATTACHMENT_THUMB_PX,
                      height: COMPOSER_ATTACHMENT_THUMB_PX,
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={src}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 10,
                        display: 'block',
                        opacity: attachment.uploading ? 0.55 : 1,
                      }}
                    />
                    {attachment.uploading && (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 10,
                          background: 'rgba(255,255,255,0.35)',
                          fontSize: 11,
                          fontWeight: 600,
                          color: Constants.MediumTextGray,
                        }}
                      >
                        …
                      </div>
                    )}
                    {!attachment.uploading && (
                      <div
                        role="button"
                        tabIndex={0}
                        aria-label="Remove image"
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return;
                          e.preventDefault();
                          this.removePendingAttachment(attachment.id);
                        }}
                        onClick={() => this.removePendingAttachment(attachment.id)}
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          background: 'rgba(0,0,0,0.78)',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                          zIndex: 2,
                          border: '2px solid #ffffff',
                          fontSize: 15,
                          lineHeight: 1,
                          fontWeight: 700,
                        }}
                      >
                        ×
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              flex: '0 0 auto',
            }}
          >
            <textarea
              tabIndex={-1}
              ref={(el) => (this.textareaRef = el)}
              value={message || ''}
              onPaste={(e) => {
                if (disableMessenger) return;
                this.handleComposerImagePaste(e, {
                  conversationId: conversation?._id,
                  collectionId,
                });
              }}
              onChange={(e) => {
                const el = e.target;
                this.setState({ message: el.value, moderationHint: '' }, () => this.syncComposerTextareaHeight());
              }}
              placeholder={
                messagePasteUploading
                  ? 'Uploading image…'
                  : editingMessage
                    ? 'Edit message…'
                    : pendingAttachments.length
                      ? 'Add a caption…'
                      : 'Send Message'
              }
              rows={1}
              style={{
                flex: '1 1 auto',
                minWidth: 0,
                width: '100%',
                minHeight: 36,
                maxHeight: 220,
                resize: 'none',
                overflowY: 'auto',
                border: 'none',
                outline: 'none',
                font: 'inherit',
                lineHeight: Constants.defaultLineHeight,
                background: 'transparent',
                padding: '6px 0',
                margin: 0,
                boxSizing: 'border-box',
              }}
            />
            {!editingMessage && pendingAttachments.length === 0 && this.renderMessengerAttachmentButton('composer', { messengerUploadContext, canAddComposerAttachment })}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 0.25rem 0 0.35rem',
                flexShrink: 0,
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                this.sendMessage(conversation);
              }}
            >
              <div
                style={{
                  height: '2.25rem',
                  width: '2.25rem',
                  minHeight: '2.25rem',
                  minWidth: '2.25rem',
                  borderRadius: '1.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: Constants.DarkTextColor,
                  background: incomingMutedSoft,
                  padding: '0.45rem',
                  opacity: (disableMessenger || messagePasteUploading || sending || pendingAttachments.some((a) => a.uploading)) ? 0.5 : 1,
                }}
              >
                <ArrowUpIcon style={{ width: 18, height: 18, ...iconOnSurface }} />
              </div>
            </div>
          </div>
          {moderationHint ? (
            <div style={{ fontSize: 12, lineHeight: 1.35, color: '#b45309', padding: '0 0.15rem' }}>
              {moderationHint}
            </div>
          ) : null}
        </div>
      </div>
    </div>;

    const messageIcon = chat && foundUser && <>
      <ArrowLeftIcon style={{ width: 16, height: 16, ...iconOnSurface }} />
      <UserAvatar user={foundUser} mini showLive={usersLive[foundUser._id]} />
      <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{foundUser.fullname || `@${foundUser.username || `${foundUser._id.slice(0, 8)}...`}`}</div>
    </> || chat && collection && <>
      <ArrowLeftIcon style={{ width: 16, height: 16, ...iconOnSurface }} />
      <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{collection.title}</div>
    </> || <h2 style={{ fontWeight: 500 }}>Messages</h2>;

    if (view === 'button') {
      // Mobile dock already exposes Messages; avoid duplicate bottom chrome.
      if (mobile) return deleteConfirmDialog;

      return (
      <>
        <div
          style={{
            fontWeight: 600,
            position: 'fixed',
            bottom: (mobile && !Constants.isIphone) ? '1rem' : '2rem',
            right: mobile ? '50%' : '2rem',
            transform: mobile ? 'translateX(50%)' : null,
            display: 'flex',
            justifyContent: 'center',
            zIndex: 2,
          }}
          onClick={() => Messenger.updateView('messenger')}
        >
          <Wrapper
            style={{ padding: '0.75rem 1.25rem', boxShadow: Constants.CTAShadow, gap: '0.5rem', display: 'flex', alignItems: 'center', borderRadius: '2rem', cursor: 'pointer' }}
          >
            <ChatUnfilledIcon style={{ width: 16, height: 16, position: 'relative', ...iconOnSurface }}>
              {atleastOneUnread && <IconBadgeDot />}
            </ChatUnfilledIcon>
            <div style={{ fontSize: mobile ? Constants.SemiSmallFontSize : Constants.NormalFontSize }}>Messages</div>
            {atleastOneUnread && <div style={{ display: 'flex', alignItems: 'center', marginLeft: '0.5rem', gap: '0.25rem' }}>
              {Object.values(conversationsUnread).map((userId, i) => {
                if (i > 2) return null;
                const user = users.find(({ _id }) => _id === userId);
                if (!user) return null;
                return (
                  <UserAvatar key={userId} user={user} mini showLive={usersLive[user._id]} />
                )
              })}
            </div>}
          </Wrapper>
        </div>
        {deleteConfirmDialog}
      </>
      );
    }

    if (!mobile && view === 'messenger') return (
      <>
        <Wrapper
          style={{ position: 'fixed', bottom: mobile ? '1rem' : '2rem', right: '2rem', zIndex: 2, boxShadow: Constants.CTAShadow, gap: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '1rem', overflow: 'hidden' }}>
          <div style={{ height: 500, width: 320 }}>
            <div style={{ display: 'flex', alignItems: 'center', padding: mobileView ? '0rem 0.5rem 0 1rem' : '0rem 1.5rem 0 2rem', cursor: 'pointer', height: 56, borderBottom: Constants.LightBorder, position: 'relative', zIndex: 2, background: Constants.SurfaceColor }}>
              <div
                style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', height: 56, minWidth: 0 }}
                onClick={() => { this.setState({ showScrollDownButton: false }); Messenger.updateView(Messenger.view); }}
              >
                {messageIcon}
              </div>
              <div style={{ padding: '0.5rem' }} onClick={() => Messenger.updateView('fullscreen', Messenger.userId, Messenger.conversationId, Messenger.collectionId)}>
                <ExpandIcon style={{ width: 20, height: 20, ...iconOnSurface }} />
              </div>
              <div style={{ padding: '0.5rem' }} onClick={() => Messenger.updateView('button')}>
                <CloseIcon style={{ width: 20, height: 20, ...iconOnSurface }} />
              </div>
            </div>
            <div style={{ position: 'absolute', inset: '3.5rem 0 0 0' }}>{chat && <ChatWrapper style={{ position: 'absolute', inset: '0 0 0 0' }}>{chat}</ChatWrapper> || userConversations}</div>
          </div>
        </Wrapper>
        {deleteConfirmDialog}
      </>
    );

    return (
      <>
        <Wrapper style={{ position: 'fixed', inset: 0, zIndex: 4 }}>
          <div style={{ position: 'absolute', inset: tablet ? 'calc(3.5rem + var(--logictry-popup-safe-top, 0px)) 0 0 0' : 'calc(3.5rem + var(--logictry-popup-safe-top, 0px)) calc(100% - 320px) 0 0' }}>
            <div style={{ position: 'absolute', top: '1rem', right: 0, bottom: '2rem', width: 1, background: Constants.LightBorderColor }} />
            {userConversations}
          </div>
          {(chat || !tablet) && <ChatWrapper style={{
            position: 'absolute',
            background: Constants.SurfaceColor,
            inset: tablet ? 'calc(3.5rem + var(--logictry-popup-safe-top, 0px)) 0 0 0' : 'calc(3.5rem + var(--logictry-popup-safe-top, 0px)) 0 0 320px',
            ...style,
          }}>
            {chat || <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                position: 'absolute',
                inset: 0,
                margin: 'auto'
              }}
            >
              <span style={{ textAlign: 'center' }}>
                <h2>Your Conversations</h2>
                <p>Click on a user to get started</p>
              </span>
            </div>}
          </ChatWrapper>}
          <div style={{ display: 'flex', alignItems: 'center', position: 'absolute', left: 0, top: 'var(--logictry-popup-safe-top, 0px)', right: 0, padding: mobile ? '0 0.5rem 0 1rem' : '0 1.5rem 0 2rem', cursor: 'pointer', background: Constants.SurfaceColor, height: 56, borderBottom: Constants.LightBorder, zIndex: 2 }}>
            <div
              onClick={() => { this.setState({ showScrollDownButton: false }); Messenger.updateView(Messenger.view); }}
              style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', height: 56, minWidth: 0 }}
            >
              {messageIcon}
            </div>
            {!mobile && <div style={{ padding: '0.5rem' }} onClick={() => Messenger.updateView('messenger', Messenger.userId, Messenger.conversationId, Messenger.collectionId)}>
              <CompressIcon style={{ width: 20, height: 20, ...iconOnSurface }} />
            </div>}
            <div style={{ padding: '0.5rem' }} onClick={() => Messenger.updateView('button')}>
              <CloseIcon style={{ width: 20, height: 20, ...iconOnSurface }} />
            </div>
          </div>
        </Wrapper>
        {deleteConfirmDialog}
      </>
    );
  }
}
